import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyOption, SurveyQuestion } from '../../types.js';

interface ImageChoiceFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: string) => void;
}

interface ImageOption extends SurveyOption {
  imageUrl?: string;
}

// 2-up (mobile) → 3-up (desktop) image grid. Selected tile gets accent border
// + soft accent ring + accent caption. Falls back to a placeholder block when
// the option has no image so the layout doesn't collapse.
export function ImageChoiceField({ palette, question, value, onChange }: ImageChoiceFieldProps): JSX.Element {
  const options = (question.settings?.options ?? []) as ImageOption[];
  const optionImages = question.settings?.optionImages ?? {};
  return (
    <div style={gridStyle()}>
      {options.map((option) => {
        const selected = value === option.value;
        const imageUrl = option.imageUrl ?? optionImages[option.value];
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className="chirp-btn"
            aria-pressed={selected}
            style={tileStyle(palette, selected)}
          >
            <div style={mediaWrapperStyle(palette, Boolean(imageUrl))}>
              {imageUrl ? (
                <img src={imageUrl} alt={option.label} style={imageStyle()} />
              ) : (
                <ImagePlaceholder color={hexWithAlpha(palette.textMuted, 0.5)} />
              )}
            </div>
            <span style={captionStyle(palette, selected)}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function gridStyle(): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  };
}

function tileStyle(palette: Palette, selected: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: 8,
    border: `1px solid ${selected ? palette.primary : palette.border}`,
    borderRadius: 12,
    background: palette.card,
    cursor: 'pointer',
    transition: 'border-color 140ms ease, box-shadow 160ms ease, transform 80ms ease',
    // Selected tile gets a soft accent halo so it's unambiguous in a busy grid.
    boxShadow: selected
      ? `0 0 0 3px ${hexWithAlpha(palette.primary, 0.18)}`
      : 'none',
  };
}

function mediaWrapperStyle(palette: Palette, hasImage: boolean): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: hasImage
      ? hexWithAlpha(palette.textMuted, 0.06)
      : `linear-gradient(135deg, ${hexWithAlpha(palette.textMuted, 0.06)} 0%, ${hexWithAlpha(palette.border, 0.4)} 100%)`,
  };
}

function imageStyle(): CSSProperties {
  return { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
}

function captionStyle(palette: Palette, selected: boolean): CSSProperties {
  return {
    marginTop: 8,
    fontSize: 13,
    fontWeight: selected ? 600 : 500,
    color: selected ? palette.primary : palette.text,
    textAlign: 'center',
    padding: '0 4px',
    wordBreak: 'break-word',
    lineHeight: 1.3,
  };
}

function ImagePlaceholder({ color }: { color: string }): JSX.Element {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  );
}
